import { LedgerEntry } from '@dataTypes/billing.types'
import { ARAgingSummaryReport, PatientARAnalytics } from '@services/api'
import {
  fetchARAging,
  fetchInsuranceARAnalytics,
  fetchLedgerEntries,
  fetchPatientARAnalytics,
  setLedgerPagination,
} from '@store/actions/billing.action'
import { AppDispatch, RootState } from '@store/index'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import {
  buildLedgerFilters,
  buildProviderNameToIdsMap,
  getSelectedProviderIds,
} from '../filterBuilders'
import { useProvidersForBilling } from '../useProvidersForBilling'
import { BillingTabType } from '../useBilling'

const EMPTY_PROVIDER_IDS: string[] = []
const LEDGER_LOADING_CONTAINER = '.ledger-table'

export interface UseLedgerOptions {
  onOpenCreateInvoiceModal?: () => void
  onChangeToInvoiceTab?: () => void
  activeTab?: BillingTabType
  suppressTableLoading?: boolean
}

export interface UseLedgerHooks {
  ledger: LedgerEntry[]
  ledgerEntries: LedgerEntry[]
  ledgerCount: number
  loadingLedger: boolean
  filteredLedger: LedgerEntry[]
  selectedLedger: LedgerEntry | null
  showModal: boolean
  relatedLedgerEntries: LedgerEntry[]
  patientARAnalytics: PatientARAnalytics | null
  insuranceARAnalytics: PatientARAnalytics | null
  arAgingReport: ARAgingSummaryReport | null
  ledgerFilterConfig: {
    filters: Record<string, string[]>
    labels: Record<string, string>
  }
  initialSelectedEntry: LedgerEntry | null
  setInitialSelectedEntry: (entry: LedgerEntry | null) => void
  handleCreateInvoiceFromLedger: (entry: LedgerEntry) => void
  handleView: (row: LedgerEntry) => void
  handleModalClose: () => void
  currentPage: number
  pageSize: number
  handlePageChange: (page: number) => void
  handlePageSizeChange: (pageSize: number) => void
  handleSortChange: (sort: string) => void
  handleTableSortChange: (sort: {
    key: string
    direction: SortDirection
  }) => void
}

export type LedgerTableHooks = Pick<
  UseLedgerHooks,
  | 'filteredLedger'
  | 'ledgerCount'
  | 'currentPage'
  | 'pageSize'
  | 'handlePageChange'
  | 'handlePageSizeChange'
  | 'handleTableSortChange'
  | 'selectedLedger'
  | 'showModal'
  | 'handleView'
  | 'handleModalClose'
  | 'relatedLedgerEntries'
  | 'handleCreateInvoiceFromLedger'
>

const useLedger = (
  searchQuery: string,
  filters: Record<string, string[]>,
  options?: UseLedgerOptions,
): UseLedgerHooks => {
  const dispatch = useDispatch<AppDispatch>()
  const isLedgerActive = options?.activeTab === BillingTabType.Ledger
  const suppressTableLoadingRef = useRef(options?.suppressTableLoading)
  const skipActiveTabOverlayRef = useRef(true)
  suppressTableLoadingRef.current = options?.suppressTableLoading
  const { ledger, ledgerCount, loadingLedger, ledgerPagination } = useSelector(
    (state: RootState) => state.billing,
  )
  const { currentPage, pageSize, sort } = ledgerPagination
  const patientARAnalytics = useSelector(
    (state: RootState) => state.billing.patientARAnalytics,
  )
  const insuranceARAnalytics = useSelector(
    (state: RootState) => state.billing.insuranceARAnalytics,
  )
  const arAgingReport = useSelector(
    (state: RootState) => state.billing.arAgingReport,
  )

  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)

  const [selectedLedger, setSelectedLedger] = useState<LedgerEntry | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [initialSelectedEntry, setInitialSelectedEntry] =
    useState<LedgerEntry | null>(null)
  const providers = useProvidersForBilling()

  const selectedPatient = useSelector(
    (state: RootState) => state.patient.selectedPatient,
  )
  const selectedClinic = useSelector(
    (state: RootState) => state.clinic.selectedClinic,
  )
  const providerFilterKey = useMemo(
    () => [...(filters.providerName ?? [])].sort().join('|'),
    [filters.providerName],
  )
  const lastQueryRef = useRef({
    search: '',
    providerFilterKey: '',
  })
  const inFlightLedgerRequestsRef = useRef(0)
  const providerNameToIdsMap = useMemo(
    () => buildProviderNameToIdsMap(providers),
    [providers],
  )
  const hasProviderFilter = Boolean(providerFilterKey)
  const selectedProviderIds = useMemo(() => {
    if (!hasProviderFilter) {
      return EMPTY_PROVIDER_IDS
    }
    return getSelectedProviderIds(filters.providerName, providerNameToIdsMap)
  }, [hasProviderFilter, filters.providerName, providerNameToIdsMap])

  const listQueryFilters = useMemo(
    () =>
      buildLedgerFilters({
        search: debouncedSearchQuery,
        selectedProviderIds,
        sort,
        selectedPatientId: selectedPatient?.patient_id,
        selectedClinicId: selectedClinic?.clinic_id,
      }),
    [
      debouncedSearchQuery,
      selectedProviderIds,
      sort,
      selectedPatient?.patient_id,
      selectedClinic?.clinic_id,
    ],
  )

  useEffect(() => {
    if (!isLedgerActive) return

    dispatch(fetchPatientARAnalytics())
    dispatch(fetchInsuranceARAnalytics())
    dispatch(fetchARAging())
  }, [
    dispatch,
    isLedgerActive,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
  ])

  useEffect(() => {
    if (!isLedgerActive) return

    dispatch(setLedgerPagination({ currentPage: 1 }))
  }, [
    dispatch,
    isLedgerActive,
    debouncedSearchQuery,
    providerFilterKey,
    selectedPatient?.patient_id,
    selectedClinic?.clinic_id,
  ])

  useEffect(() => {
    if (isLedgerActive) {
      skipActiveTabOverlayRef.current = true
    }
  }, [isLedgerActive])

  useEffect(() => {
    if (!isLedgerActive) return

    const searchChanged = lastQueryRef.current.search !== debouncedSearchQuery
    const providerFilterChanged =
      lastQueryRef.current.providerFilterKey !== providerFilterKey

    if ((searchChanged || providerFilterChanged) && currentPage !== 1) {
      return
    }

    let cancelled = false

    const loadLedger = async (): Promise<void> => {
      const shouldShowLoading = inFlightLedgerRequestsRef.current === 0
      inFlightLedgerRequestsRef.current += 1

      const suppressOverlay =
        suppressTableLoadingRef.current || skipActiveTabOverlayRef.current
      if (shouldShowLoading && !suppressOverlay) {
        showLoading({ container: LEDGER_LOADING_CONTAINER })
      }

      try {
        await dispatch(
          fetchLedgerEntries(
            Object.keys(listQueryFilters).length > 0
              ? listQueryFilters
              : undefined,
          ),
        ).unwrap()
      } finally {
        inFlightLedgerRequestsRef.current -= 1

        if (!cancelled) {
          lastQueryRef.current = {
            search: debouncedSearchQuery,
            providerFilterKey,
          }
        }

        if (inFlightLedgerRequestsRef.current === 0) {
          skipActiveTabOverlayRef.current = false
          hideLoading(LEDGER_LOADING_CONTAINER)
        }
      }
    }

    void loadLedger()

    return () => {
      cancelled = true
    }
  }, [
    dispatch,
    isLedgerActive,
    currentPage,
    pageSize,
    listQueryFilters,
    debouncedSearchQuery,
    providerFilterKey,
  ])

  const filteredLedger = useMemo(() => ledger, [ledger])

  const handleCreateInvoiceFromLedger = useCallback(
    (entry: LedgerEntry): void => {
      options?.onChangeToInvoiceTab?.()
      setInitialSelectedEntry(entry)
      requestAnimationFrame(() => {
        options?.onOpenCreateInvoiceModal?.()
      })
    },
    [options],
  )

  const ledgerFilterConfig = useMemo(
    () => ({
      filters: {
        providerName: Array.from(
          new Set(
            providers.map((provider) => {
              return `Dr. ${provider.first_name} ${provider.last_name}`
            }),
          ),
        ).sort(),
      },
      labels: {
        providerName: 'Provider',
      },
    }),
    [providers],
  )

  const relatedLedgerEntries = useMemo(() => {
    if (!selectedLedger) return []
    return ledger.filter(
      (entry) => entry.patientName === selectedLedger.patientName,
    )
  }, [ledger, selectedLedger])

  const handleView = useCallback((row: LedgerEntry): void => {
    setSelectedLedger(row)
    setShowModal(true)
  }, [])

  const handleModalClose = useCallback((): void => {
    setShowModal(false)
  }, [])

  const handlePageChange = useCallback(
    (page: number): void => {
      dispatch(setLedgerPagination({ currentPage: page }))
    },
    [dispatch],
  )

  const handlePageSizeChange = useCallback(
    (newPageSize: number): void => {
      dispatch(setLedgerPagination({ pageSize: newPageSize, currentPage: 1 }))
    },
    [dispatch],
  )

  const handleSortChange = useCallback(
    (newSort: string): void => {
      dispatch(setLedgerPagination({ sort: newSort, currentPage: 1 }))
    },
    [dispatch],
  )

  const handleTableSortChange = useCallback(
    ({ key, direction }: { key: string; direction: SortDirection }): void => {
      if (key === 'patientName') {
        handleSortChange(`patientName:${direction}`)
      } else if (key === 'providerName') {
        handleSortChange(`providerName:${direction}`)
      } else if (key === 'appointmentDate') {
        handleSortChange(`appointmentDate:${direction}`)
      }
    },
    [handleSortChange],
  )

  return {
    ledger,
    ledgerEntries: ledger,
    ledgerCount,
    loadingLedger,
    filteredLedger,
    selectedLedger,
    showModal,
    relatedLedgerEntries,
    patientARAnalytics,
    insuranceARAnalytics,
    arAgingReport,
    ledgerFilterConfig,
    initialSelectedEntry,
    setInitialSelectedEntry,
    handleCreateInvoiceFromLedger,
    handleView,
    handleModalClose,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleTableSortChange,
  }
}

export default useLedger
